import { apiGet, apiPost, apiDelete, apiPatch } from '../../../lib/api/axios';
import { ResourcePermission, ResourcePermissionRole, UserSummary } from '../../chat/types';
import type { WritingUploadSubtype, SharedWritingDocument } from './types';
import {
  WritingDocument,
  DownloadLinkResponse,
  UploadDocumentResponse,
  UploadStatusResponse,
  UploadDocumentType,
  SasResponse,
  MeetingDocumentStatusResponse
} from './types';

export interface WritingPermissionsResponse {
  current_user_permission: ResourcePermissionRole;
  shared_with?: ResourcePermission[];
  group_shares?: { group_id: string; group_name: string; permission: ResourcePermissionRole }[];
  shared_by?: UserSummary;
}

export async function getWritingDocuments(): Promise<WritingDocument[]> {
  const response = await apiGet('/writing/');
  return response.data;
}

export async function getSharedWritingDocuments(): Promise<SharedWritingDocument[]> {
  const response = await apiGet('/writing/shared');
  return response.data;
}

export async function getWritingDocumentLink(documentId: string): Promise<DownloadLinkResponse> {
  const response = await apiGet(`/writing/${documentId}/download-link`);
  return response.data;
}

export async function getWritingTemplateLink(templateType: string): Promise<DownloadLinkResponse> {
  const response = await apiGet(`/writing/templates/${templateType}/download-link`);
  return response.data;
}

export async function getWritingUploadDocumentLink(externalWritingUploadId: string): Promise<DownloadLinkResponse> {
  const response = await apiGet(`/writing/uploads/${externalWritingUploadId}/download-link`);
  return response.data;
}

export async function deactivateWritingDocument(externalWritingId: string): Promise<void> {
  await apiDelete(`/writing/${externalWritingId}`);
}

export async function createUploadFromDocument(
  documentId: string,
  documentType: UploadDocumentType,
  uploadType?: WritingUploadSubtype
): Promise<UploadDocumentResponse> {
  const params = new URLSearchParams();
  params.append('document_type', documentType);
  if (uploadType) {
    params.append('upload_type', uploadType);
  }
  const response = await apiPost(`/writing/uploads/${documentId}?${params.toString()}`, null, {
    meta: {
      globalError: false,
      context: 'create-upload-from-document'
    }
  });
  return response.data;
}

export async function archiveWritingUpload(externalWritingUploadId: string): Promise<void> {
  await apiDelete(`/writing/uploads/${externalWritingUploadId}`, {
    meta: {
      globalError: false,
      context: 'archive-writing-upload'
    }
  });
}

export async function getMeetingDocumentStatus(externalWritingId: string): Promise<MeetingDocumentStatusResponse> {
  const response = await apiGet(`/writing/${externalWritingId}/status`, {
    meta: {
      globalError: false,
      context: 'get-meeting-status'
    }
  });
  return response.data;
}

export async function getUploadStatus(externalUploadId: string): Promise<UploadStatusResponse> {
  const response = await apiGet(`/writing/uploads/${externalUploadId}/status`, {
    meta: {
      globalError: false,
      context: 'get-upload-status'
    }
  });
  return response.data;
}

export async function setWritingUploadType(
  externalWritingUploadId: string,
  newType: WritingUploadSubtype
): Promise<void> {
  await apiPost(`/writing/uploads/${externalWritingUploadId}/type`, newType, {
    meta: {
      globalError: false,
      context: 'set-writing-upload-type'
    }
  });
}

export async function getDocumentLinkByExternalId(externalDocumentId: string): Promise<SasResponse> {
  const response = await apiGet(`/files/${externalDocumentId}/link?original=true`, {
    meta: {
      globalError: false,
      context: 'get-document-link-external'
    }
  });
  return response.data;
}

export async function getWritingDocumentVersions(externalWritingId: string): Promise<{
  base_document_id: number;
  versions: { external_document_id: string; version_id: number; title: string; created_at: string }[];
}> {
  const response = await apiGet(`/writing/${externalWritingId}/versions`, {
    meta: {
      globalError: false,
      context: 'get-writing-versions'
    }
  });
  return response.data;
}

export async function resetWritingConversation(externalWritingId: string): Promise<void> {
  await apiDelete(`/writing/${externalWritingId}/conversation`, {
    meta: {
      globalError: false,
      context: 'reset-writing-conversation'
    }
  });
}

// Sharing API functions
export async function getWritingPermissions(externalWritingId: string): Promise<WritingPermissionsResponse> {
  const response = await apiGet(`/writing/${externalWritingId}/permissions`, {
    meta: {
      globalError: false,
      context: 'get-writing-permissions'
    }
  });
  return response.data;
}

export async function addPeopleToWriting(
  externalWritingId: string,
  message: string,
  people: string[],
  permission: ResourcePermissionRole,
  groupIds?: string[]
): Promise<ResourcePermission[]> {
  const response = await apiPost(
    `/writing/${externalWritingId}/shares`,
    {
      emails: people.length > 0 ? people : undefined,
      permission: permission,
      message: message,
      group_ids: groupIds
    },
    {
      meta: {
        globalError: false,
        context: 'add-people-to-writing'
      }
    }
  );
  return response.data;
}

export async function deleteWritingShare(externalWritingId: string, permissionId: string): Promise<void> {
  await apiDelete(`/writing/${externalWritingId}/shares/${permissionId}`, {
    meta: {
      globalError: false,
      context: 'delete-writing-share'
    }
  });
}

export async function deleteWritingGroupShare(externalWritingId: string, groupId: string): Promise<void> {
  await apiDelete(`/writing/${externalWritingId}/group-shares/${groupId}`, {
    meta: {
      globalError: false,
      context: 'delete-writing-group-share'
    }
  });
}

export async function updateWritingShare(
  externalWritingId: string,
  permissionId: string,
  permission: ResourcePermissionRole
): Promise<void> {
  await apiPatch(`/writing/${externalWritingId}/shares/${permissionId}?role=${permission}`, {
    meta: {
      globalError: false,
      context: 'update-writing-share'
    }
  });
}
